/**
 * layoutExportBundle — the ONE neutral geometry bundle every studio/site
 * exporter consumes (KMZ, shapefile, DXF, DWG-via-DXF).
 *
 * A bundle is a flat feature list in WGS84 with the screening metadata the
 * exports carry (name, kind, category, area, source). It can be built from
 * either a live parcel test fit (studio export: components + exclusions +
 * acoustic barriers + parcel ring) or from Site Map layers (map export).
 * Pure and deterministic — exporters own all formatting.
 */
import type { GenerationParcelTestFitResult } from '../../lib/generationImpact/generationLayoutGeometry';
import type {
  GenerationImportedGeometryRef,
  GenerationImportedLayerAssignment,
  GenerationImportedLayerOrigin,
} from '../../types/generationImpact';
import type { SiteLayer } from '../../types/site';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';

export type ExportGeometryType = 'polygon' | 'line' | 'point';
export type LayoutExportProperty = string | number | boolean | null;

export interface LayoutExportFeature {
  /** Stable source identity used for round-trip review and retry-safe imports. */
  id: string;
  name: string;
  /** Component kind / zone kind / layer category discriminator for layers. */
  kind: string;
  category: string;
  geometry: {
    type: ExportGeometryType;
    coordinates: Array<{ lat: number; lng: number }>;
    holes?: Array<Array<{ lat: number; lng: number }>>;
  };
  areaAcres?: number;
  source: string;
  /** Allowlisted screening metadata; arbitrary source attributes never leak. */
  properties?: Readonly<Record<string, LayoutExportProperty>>;
}

export interface LayoutExportVerificationIssue {
  id: 'packing' | 'containment' | 'setback' | 'exclusions' | 'collisions';
  label: string;
  state: 'warning' | 'unknown';
  /** Stable bundle feature ids that locate the finding on a map/CAD layer. */
  featureIds: string[];
}

export interface LayoutExportBundle {
  schemaVersion: 2;
  /** Basename stem for generated files (no extension). */
  name: string;
  manifest: {
    crs: 'EPSG:4326';
    localUnits: 'm';
    siteId?: string;
    scenarioId?: string;
    /** Saved scenario updatedAt/hash label, or "Unsaved draft". */
    revision?: string;
    origin?: { lat: number; lng: number };
    /** Imported-source → Studio-role/equipment-target map (role-independent ids). */
    studioAssignments?: ReadonlyArray<{
      id: string;
      sourceLabel: string;
      sourceContentHash: string;
      geometryType: GenerationImportedLayerAssignment['geometryType'];
      studioRole: GenerationImportedLayerAssignment['role'];
      componentId?: string;
    }>;
    verification?: {
      status: GenerationParcelTestFitResult['status'];
      containmentPct: number | null;
      parcelEdgeSetbackSatisfied: boolean | null;
      exclusionOverlapAcres?: number | null;
      obstacleOverlapAcres?: number | null;
      exclusionAnalysis: 'not-applied' | 'unavailable' | 'partial' | 'unknown' | 'clear' | 'overlap';
      /** FEMA screening state behind the exclusion set; a 'partial' fit is
       * never reported as clear, and its reason (including any geometry
       * fallback disclosure) travels with the bundle. */
      femaScreening?: { status: 'applied' | 'partial' | 'unavailable' | 'not-fetched' | 'disabled'; reason?: string };
      collisionAnalysis: 'not-applied' | 'unknown' | 'clear' | 'overlap';
      issues: LayoutExportVerificationIssue[];
    };
  };
  features: LayoutExportFeature[];
}

export interface TestFitBundleOptions {
  siteId?: string;
  scenarioId?: string;
  revision?: string;
  /** Visible reference layers included in the Studio export review. */
  referenceLayers?: readonly SiteLayer[];
  /** Explicit multi-parcel list override when exporting from Studio/Site Map. */
  parcels?: readonly SiteBoundaryParcel[];
}

function sanitizeStem(value: string): string {
  const cleaned = value.trim().replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'layout-export';
}

function verificationIssuesForTestFit(
  testFit: GenerationParcelTestFitResult,
): LayoutExportVerificationIssue[] {
  const parcelFeatureId = testFit.parcel ? `parcel:${testFit.parcel.id}` : 'parcel:target';
  const componentFeatureIds = (
    predicate: (component: GenerationParcelTestFitResult['components'][number]) => boolean,
  ): string[] => testFit.components
    .filter(predicate)
    .map(component => `component:${component.rectangle.id}`);
  const fallback = (ids: string[]): string[] => ids.length > 0 ? ids : [parcelFeatureId];
  const issues: LayoutExportVerificationIssue[] = [];

  if (testFit.plan?.status === 'overflow') {
    issues.push({
      id: 'packing',
      label: 'Packing envelope',
      state: 'warning',
      featureIds: [parcelFeatureId],
    });
  }
  if (testFit.outsideAcres === null || !testFit.parcel) {
    issues.push({
      id: 'containment',
      label: 'Boundary containment',
      state: 'unknown',
      featureIds: [parcelFeatureId],
    });
  } else if (testFit.outsideAcres > 0.01) {
    issues.push({
      id: 'containment',
      label: 'Boundary containment',
      state: 'warning',
      featureIds: fallback(componentFeatureIds(component => (component.outsideAcres ?? 0) > 0.01)),
    });
  }
  if (testFit.parcelEdgeSetbackSatisfied !== true) {
    issues.push({
      id: 'setback',
      label: 'Parcel-edge setback',
      state: testFit.parcelEdgeSetbackSatisfied === false ? 'warning' : 'unknown',
      featureIds: fallback(componentFeatureIds(
        component => component.meetsParcelEdgeSetback !== true,
      )),
    });
  }

  // A partial FEMA screening (including every disclosed geometry-fallback
  // merge) is unresolved, exactly as the studio verify panel treats it.
  const exclusionUnavailable = testFit.layout.exclusions?.femaStatus === 'unavailable' || testFit.layout.exclusions?.femaStatus === 'partial';
  if (
    exclusionUnavailable
    || testFit.exclusionOverlapAcres === null
    || (testFit.exclusionOverlapAcres ?? 0) > 0.01
  ) {
    issues.push({
      id: 'exclusions',
      label: 'Exclusion-zone clearance',
      state: exclusionUnavailable || testFit.exclusionOverlapAcres === null
        ? 'unknown'
        : 'warning',
      featureIds: fallback(componentFeatureIds(component => (
        component.exclusionOverlapAcres === null
        || (component.exclusionOverlapAcres ?? 0) > 0.01
      ))),
    });
  }

  if (
    testFit.obstacleOverlapAcres === null
    || (testFit.obstacleOverlapAcres ?? 0) > 0.01
  ) {
    issues.push({
      id: 'collisions',
      label: 'Fixed-component collision clearance',
      state: testFit.obstacleOverlapAcres === null ? 'unknown' : 'warning',
      featureIds: fallback(componentFeatureIds(component => (
        component.obstacleOverlapAcres === null
        || (component.obstacleOverlapAcres ?? 0) > 0.01
      ))),
    });
  }
  return issues;
}

function importRefProperties(
  importRef: GenerationImportedGeometryRef | undefined,
): Record<string, LayoutExportProperty> {
  if (!importRef) return {};
  return {
    sourceFile: importRef.fileName,
    sourceLayer: importRef.layerName ?? null,
    sourceSha256: importRef.sourceSha256 ?? null,
    cadEntityHandle: importRef.entityHandle ?? null,
    cadEntityType: importRef.entityType ?? null,
    cadDwgVersion: importRef.dwgVersion ?? null,
    cadMetresPerUnit: importRef.metresPerUnit ?? null,
    cadCrsCode: importRef.crsCode ?? null,
  };
}

/** Imported-source ownership link, so exports carry which assignment produced
 *  each derived feature (round-trips with the manifest's studioAssignments). */
function originProperties(
  origin: GenerationImportedLayerOrigin | undefined,
): Record<string, LayoutExportProperty> {
  return origin ? { importAssignmentId: origin.assignmentId } : {};
}

function customForComponent(
  testFit: GenerationParcelTestFitResult,
  componentId: string,
) {
  const customs = testFit.layout.customComponents ?? [];
  const exact = customs.find(custom => componentId === `custom-${custom.id}`);
  if (exact) return exact;
  return [...customs]
    .sort((left, right) => right.id.length - left.id.length)
    .find(custom => componentId.startsWith(`custom-${custom.id}-`));
}

/** Studio bundle: everything the current test fit places on the ground. */
export function bundleFromTestFit(
  testFit: GenerationParcelTestFitResult,
  name: string,
  options: TestFitBundleOptions = {},
): LayoutExportBundle {
  const features: LayoutExportFeature[] = [];

  const parcelsToExport: readonly SiteBoundaryParcel[] =
    options.parcels && options.parcels.length > 0
      ? options.parcels
      : testFit.parcels && testFit.parcels.length > 0
        ? testFit.parcels
        : testFit.parcel
          ? [testFit.parcel]
          : [];

  for (let pi = 0; pi < parcelsToExport.length; pi++) {
    const parcel = parcelsToExport[pi]!;
    if (!Array.isArray(parcel.polygon) || parcel.polygon.length < 3) continue;
    const rawLabel = parcel.label?.trim();
    const featureName = parcelsToExport.length === 1
      ? (rawLabel || 'Parcel boundary')
      : rawLabel
        ? (/parcel|boundary|site|outline/i.test(rawLabel) ? rawLabel : `Parcel ${pi + 1} — ${rawLabel}`)
        : `Parcel ${pi + 1}`;
    features.push({
      id: `parcel:${parcel.id}`,
      name: featureName,
      kind: 'parcel-boundary',
      category: 'boundary',
      geometry: {
        type: 'polygon',
        coordinates: parcel.polygon.map(point => ({ ...point })),
        ...(parcel.holes && parcel.holes.length > 0
          ? { holes: parcel.holes.map(ring => ring.map(point => ({ ...point }))) }
          : {}),
      },
      areaAcres: parcel.area,
      source: 'site-boundary',
    });
  }

  for (const component of testFit.components) {
    const custom = customForComponent(testFit, component.rectangle.id);
    features.push({
      id: `component:${component.rectangle.id}`,
      name: component.rectangle.label,
      kind: component.rectangle.kind,
      category: 'component',
      geometry: { type: 'polygon', coordinates: component.coordinates.map(point => ({ ...point })) },
      areaAcres: component.rectangle.areaAcres,
      source: custom?.source ?? component.rectangle.dimensionBasis,
      properties: {
        widthM: component.rectangle.widthM,
        heightM: component.rectangle.heightM,
        rotationDeg: component.rectangle.rotationDeg ?? 0,
        lifecycle: component.rectangle.lifecycle,
        placement: component.rectangle.placement ?? 'packed',
        outsideAcres: component.outsideAcres,
        exclusionOverlapAcres: component.exclusionOverlapAcres ?? null,
        obstacleOverlapAcres: component.obstacleOverlapAcres ?? null,
        ...(custom ? {
          customId: custom.id,
          ...importRefProperties(custom.importRef),
          ...originProperties(custom.origin),
        } : {}),
      },
    });
  }

  for (const zone of testFit.layout.exclusions?.zones ?? []) {
    zone.polygons.forEach((ring, ringIndex) => {
      if (ring.length < 3) return;
      features.push({
        id: `exclusion:${zone.id}:${ringIndex}`,
        name: zone.polygons.length > 1 ? `${zone.label} (${ringIndex + 1})` : zone.label,
        kind: zone.kind,
        category: 'exclusion',
        geometry: { type: 'polygon', coordinates: ring.map(point => ({ ...point })) },
        source: zone.source,
        properties: {
          ...importRefProperties(zone.importRef), ...originProperties(zone.origin),
          // Geometry-fallback disclosure travels in the attribute table every
          // exporter (KML ExtendedData, DXF comment, manifest) serialises.
          ...(zone.geometryTier ? { geometryTier: zone.geometryTier } : {}),
          ...(zone.precisionSnapDeg !== undefined ? { precisionSnapDeg: zone.precisionSnapDeg } : {}),
        },
      });
    });
  }

  for (const barrier of testFit.layout.acousticBarriers ?? []) {
    features.push({
      id: `barrier:${barrier.id}`,
      name: barrier.label,
      kind: barrier.kind,
      category: 'acoustic-barrier',
      geometry: {
        type: barrier.kind === 'building' ? 'polygon' : 'line',
        coordinates: barrier.path.map(point => ({ ...point })),
      },
      source: barrier.importRef ? 'imported' : 'user-drawn',
      properties: {
        heightM: barrier.heightM,
        barrierKind: barrier.kind,
        createdAt: barrier.createdAt ?? null,
        ...importRefProperties(barrier.importRef),
        ...originProperties(barrier.origin),
      },
    });
  }

  for (const custom of testFit.layout.customComponents ?? []) {
    if (custom.shape.type !== 'polyline' || custom.shape.path.length < 2) continue;
    features.push({
      id: `custom-centerline:${custom.id}`,
      name: `${custom.label} centerline`,
      kind: custom.kind,
      category: 'component-centerline',
      geometry: {
        type: 'line',
        coordinates: custom.shape.path.map(point => ({ ...point })),
      },
      source: custom.source ?? 'user-drawn',
      properties: {
        corridorWidthM: custom.shape.widthM,
        lifecycle: custom.lifecycle,
        ...importRefProperties(custom.importRef),
        ...originProperties(custom.origin),
      },
    });
  }

  if (testFit.layout.frontline) {
    features.push({
      id: 'frontline',
      name: 'Frontline alignment',
      kind: 'frontline',
      category: 'alignment',
      geometry: {
        type: 'line',
        coordinates: [
          { ...testFit.layout.frontline.start },
          { ...testFit.layout.frontline.end },
        ],
      },
      source: 'user-drawn',
      properties: { layoutRotationDeg: testFit.layout.rotationDeg },
    });
  }

  for (const layer of options.referenceLayers ?? []) {
    if (layer.visible === false || layer.geometry.coordinates.length === 0) continue;
    features.push(siteLayerFeature(layer, 'reference'));
  }

  return {
    schemaVersion: 2,
    name: sanitizeStem(name),
    manifest: {
      crs: 'EPSG:4326',
      localUnits: 'm',
      ...(options.siteId ? { siteId: options.siteId } : {}),
      ...(options.scenarioId ? { scenarioId: options.scenarioId } : {}),
      ...(options.revision ? { revision: options.revision } : {}),
      origin: { ...testFit.anchor },
      ...((testFit.layout.importedAssignments ?? []).length > 0
        ? {
            studioAssignments: (testFit.layout.importedAssignments ?? []).map(assignment => ({
              id: assignment.id,
              sourceLabel: assignment.sourceLabel,
              sourceContentHash: assignment.sourceContentHash,
              geometryType: assignment.geometryType,
              studioRole: assignment.role,
              ...(assignment.componentId ? { componentId: assignment.componentId } : {}),
            })),
          }
        : {}),
      verification: {
        status: testFit.status,
        containmentPct: testFit.containmentPct,
        parcelEdgeSetbackSatisfied: testFit.parcelEdgeSetbackSatisfied,
        ...(testFit.exclusionOverlapAcres !== undefined
          ? { exclusionOverlapAcres: testFit.exclusionOverlapAcres }
          : {}),
        ...(testFit.obstacleOverlapAcres !== undefined
          ? { obstacleOverlapAcres: testFit.obstacleOverlapAcres }
          : {}),
        ...(testFit.layout.exclusions?.femaStatus
          ? { femaScreening: { status: testFit.layout.exclusions.femaStatus, ...(testFit.layout.exclusions.femaUnavailableReason ? { reason: testFit.layout.exclusions.femaUnavailableReason } : {}) } } : {}),
        exclusionAnalysis: testFit.layout.exclusions?.femaStatus === 'unavailable'
          ? 'unavailable'
          : testFit.exclusionOverlapAcres === undefined
            ? 'not-applied'
          : testFit.exclusionOverlapAcres === null
            ? 'unknown'
          : testFit.layout.exclusions?.femaStatus === 'partial' && !(testFit.exclusionOverlapAcres > 0.01)
            ? 'partial'
            : testFit.exclusionOverlapAcres > 0.01 ? 'overlap' : 'clear',
        collisionAnalysis: testFit.obstacleOverlapAcres === undefined
          ? 'not-applied'
          : testFit.obstacleOverlapAcres === null
            ? 'unknown'
            : testFit.obstacleOverlapAcres > 0.01 ? 'overlap' : 'clear',
        issues: verificationIssuesForTestFit(testFit),
      },
    },
    features,
  };
}

function siteLayerFeature(layer: SiteLayer, categoryPrefix?: string): LayoutExportFeature {
  const category = categoryPrefix ? `${categoryPrefix}:${layer.category}` : layer.category;
  return {
    id: `site-layer:${layer.id}`,
    name: layer.name,
    kind: String(layer.attributes?.layoutComponentKind ?? layer.category),
    category,
    geometry: {
      type: layer.geometry.type,
      coordinates: layer.geometry.coordinates.map(point => ({ ...point })),
      ...(layer.geometry.holes && layer.geometry.holes.length > 0
        ? { holes: layer.geometry.holes.map(ring => ring.map(point => ({ ...point }))) }
        : {}),
    },
    ...(typeof layer.attributes?.layoutAreaAcres === 'number'
      ? { areaAcres: layer.attributes.layoutAreaAcres }
      : {}),
    source: layer.source,
    properties: {
      layerId: layer.id,
      importedAt: layer.importedAt,
      visible: layer.visible !== false,
      sourceScenarioId: typeof layer.attributes?.scenarioId === 'string'
        ? layer.attributes.scenarioId
        : null,
      contentHash: layer.contentHash ?? null,
      cadSourceFile: layer.attributes?.cadSourceFile ?? null,
      cadSourceSha256: layer.attributes?.cadSourceSha256 ?? null,
      cadEntityHandle: layer.attributes?.cadEntityHandle ?? null,
      cadEntityType: layer.attributes?.cadEntityType ?? null,
      cadDwgVersion: layer.attributes?.cadDwgVersion ?? null,
      cadLayer: layer.attributes?.cadLayer ?? null,
      cadMetresPerUnit: layer.attributes?.cadMetersPerUnit ?? null,
      cadCrsCode: layer.attributes?.cadCrsCode ?? null,
      cadAnchorLat: layer.attributes?.cadAnchorLat ?? null,
      cadAnchorLng: layer.attributes?.cadAnchorLng ?? null,
      cadOriginX: layer.attributes?.cadOriginX ?? null,
      cadOriginY: layer.attributes?.cadOriginY ?? null,
      cadRotationDeg: layer.attributes?.cadRotationDeg ?? null,
      cadGeoreferenceSource: layer.attributes?.cadGeoreferenceSource ?? null,
      cadProjectedReferenceXM: layer.attributes?.cadProjectedReferenceXM ?? null,
      cadProjectedReferenceYM: layer.attributes?.cadProjectedReferenceYM ?? null,
    },
  };
}

/** Site Map bundle: site boundary parcels (when provided) + visible (or all) overlay layers. */
export function bundleFromSiteLayers(
  layers: readonly SiteLayer[],
  name: string,
  parcels: readonly SiteBoundaryParcel[] = [],
): LayoutExportBundle {
  const parcelFeatures: LayoutExportFeature[] = [];
  for (let pi = 0; pi < parcels.length; pi++) {
    const parcel = parcels[pi]!;
    if (!Array.isArray(parcel.polygon) || parcel.polygon.length < 3) continue;
    const rawLabel = parcel.label?.trim();
    const featureName = parcels.length === 1
      ? (rawLabel || 'Parcel boundary')
      : rawLabel
        ? (/parcel|boundary|site|outline/i.test(rawLabel) ? rawLabel : `Parcel ${pi + 1} — ${rawLabel}`)
        : `Parcel ${pi + 1}`;
    parcelFeatures.push({
      id: `parcel:${parcel.id}`,
      name: featureName,
      kind: 'parcel-boundary',
      category: 'boundary',
      geometry: {
        type: 'polygon',
        coordinates: parcel.polygon.map(point => ({ ...point })),
        ...(parcel.holes && parcel.holes.length > 0
          ? { holes: parcel.holes.map(ring => ring.map(point => ({ ...point }))) }
          : {}),
      },
      areaAcres: parcel.area,
      source: 'site-boundary',
    });
  }
  const features: LayoutExportFeature[] = [
    ...parcelFeatures,
    ...layers
      .filter(layer => layer.geometry.coordinates.length > 0)
      .map(layer => siteLayerFeature(layer)),
  ];
  return {
    schemaVersion: 2,
    name: sanitizeStem(name),
    manifest: { crs: 'EPSG:4326', localUnits: 'm' },
    features,
  };
}
